import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import { Save, Delete } from "@material-ui/icons";
import firebase from "../firebase";

const SESSION_TYPES = [
  { value: "RegularSession", label: "Regular Session" },
  { value: "MainSession", label: "Main Session" },
  { value: "GroupSession", label: "Group Session" },
];

const toInputDate = (d) => {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const fromInputDate = (value) => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Add/edit a session. Reads ?sessionID= to edit, or ?kidID= to prefill the kid
// (used by the "Log session" shortcuts on the Kids page and kid profile).
// onKidChange lets the parent page show context for the selected kid.
export default function SessionsForm({ onKidChange }) {
  const history = useHistory();
  const db = firebase.firestore();

  const params = new URLSearchParams(window.location.search);
  const initialSessionID = params.get("sessionID") || "";
  const prefillKidID = params.get("kidID") || "";
  const readOnly =
    params.get("editable") === "false" ||
    window.location.href.includes("sign-in") ||
    window.location.href.includes("signUp");

  const [sessID, setSessionID] = useState(initialSessionID);
  const [kid, setKid] = useState("");
  const [fullName, setFullName] = useState("");
  const [location, setLocation] = useState("");
  const [sessionType, setSessionType] = useState("RegularSession");
  const [date, setDate] = useState(new Date());
  const [notes, setNotes] = useState("");
  const [kidsData, setKidsData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const selectKid = (kidId, kidsList) => {
    setKid(kidId);
    if (onKidChange) onKidChange(kidId);
    const match = (kidsList || kidsData).find((k) => k.id === kidId);
    if (match) {
      setFullName(match.fullName);
      // Default the location to the kid's school when nothing is set yet.
      if (match.location) setLocation((prev) => prev || match.location);
    }
  };

  useEffect(() => {
    const base = db.collection("Main").doc(global.config.uid);

    const unsubKids = base
      .collection("Kids")
      .orderBy("LastName")
      .onSnapshot((snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          fullName: doc.data().FirstName + " " + doc.data().LastName,
          location: doc.data().Location,
        }));
        setKidsData(list);
        if (prefillKidID && initialSessionID === "") {
          selectKid(prefillKidID, list);
        }
      });

    const unsubLocations = base.collection("Locations").onSnapshot((snapshot) => {
      setLocationData(
        snapshot.docs.map((doc) => ({ id: doc.id, Location: doc.data().Location }))
      );
    });

    if (initialSessionID !== "") {
      base
        .collection("Sessions")
        .doc(initialSessionID)
        .get()
        .then((doc) => {
          if (doc.exists) {
            setKid(doc.data().Kid || "");
            if (onKidChange) onKidChange(doc.data().Kid || "");
            setFullName(doc.data().fullName || "");
            setLocation(doc.data().Location || "");
            setSessionType(doc.data().sessionType || "RegularSession");
            setNotes(doc.data().Notes || "");
            if (doc.data().Date) setDate(doc.data().Date.toDate());
          }
        })
        .catch((error) => console.error("Error getting session:", error));
    }

    return () => {
      unsubKids();
      unsubLocations();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionID]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    const record = {
      Kid: kid,
      Date: date,
      Notes: notes,
      Location: location,
      fullName: fullName,
      sessionType: sessionType || "RegularSession",
    };
    const sessionsRef = db.collection("Main").doc(global.config.uid).collection("Sessions");

    if (sessID !== "") {
      sessionsRef
        .doc(sessID)
        .set(record, { merge: true })
        .then(() => {
          setSaving(false);
          setToast({ severity: "success", message: "Session updated" });
        })
        .catch((error) => {
          setSaving(false);
          console.error("Error writing session:", error);
          setToast({ severity: "error", message: "Could not save — try again" });
        });
    } else {
      sessionsRef
        .add(record)
        .then((doc) => {
          setSessionID(doc.id);
          setSaving(false);
          setToast({ severity: "success", message: "Session saved" });
          history.replace("/sessions-profile?sessionID=" + doc.id);
        })
        .catch((error) => {
          setSaving(false);
          console.error("Error creating session:", error);
          setToast({ severity: "error", message: "Could not save — try again" });
        });
    }
  };

  const handleDelete = () => {
    if (!window.confirm("Are you sure you want to delete this session?")) return;
    db.collection("Main")
      .doc(global.config.uid)
      .collection("Sessions")
      .doc(sessID)
      .delete()
      .then(() => history.push("/sessions"))
      .catch((error) => console.error("Error removing session:", error));
  };

  return (
    <form autoCorrect="off" onSubmit={handleSubmit} id="single-form">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          select
          required
          fullWidth
          label="Kid"
          value={kid}
          onChange={(e) => selectKid(e.target.value)}
          disabled={readOnly}
        >
          {kidsData.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.fullName}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <TextField
            required
            fullWidth
            type="date"
            label="Date"
            value={toInputDate(date)}
            onChange={(e) => e.target.value && setDate(fromInputDate(e.target.value))}
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
          <TextField
            select
            required
            fullWidth
            label="Session Type"
            value={sessionType}
            onChange={(e) => setSessionType(e.target.value)}
            disabled={readOnly}
          >
            {SESSION_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          select
          required
          fullWidth
          label="Location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          disabled={readOnly}
        >
          {locationData.map((option) => (
            <MenuItem key={option.id} value={option.Location}>
              {option.Location}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          required
          fullWidth
          multiline
          minRows={5}
          label="Session Notes"
          placeholder="What did you work on? How did it go?"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={readOnly}
        />

        {!readOnly && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              id="save"
              variant="contained"
              color="secondary"
              size="large"
              type="submit"
              disabled={saving}
              startIcon={<Save />}
            >
              {saving ? "Saving..." : sessID ? "Save Changes" : "Save Session"}
            </Button>
            {sessID !== "" && (
              <Button
                id="delete"
                onClick={handleDelete}
                variant="outlined"
                color="error"
                size="large"
                disabled={saving}
                startIcon={<Delete />}
              >
                Delete
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>
            {toast.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </form>
  );
}
